import { useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { LogOut, Mail, Receipt, TrendingUp, User } from 'lucide-react'
import {
  AccountMenu,
  BrandLogo,
  BrandMark,
  Sidebar as SidebarShell,
  SidebarCreditChip,
  SidebarFooter,
  StrategyList,
  ThemeToggle,
} from '@/components'
import { AppContext } from '../providers/AppProvider.jsx'
import { ThemeContext } from '../providers/ThemeProvider.jsx'
import { paywallTrigger } from '../system/paywallTrigger.js'
import { botApi } from '../bot/api'
import { useSSE } from '../contexts/SSEContext.jsx'
import { isBotLifecycleEvent } from '../contexts/SSEEvents.js'
import { server } from '../pages/chat/Server.js'

const SIDEBAR_STATE_KEY = 'algobuilder_sidebar_expanded'
const AUTO_RAIL_BREAKPOINT = 1440

function makeMediaSubscription(query) {
  return {
    subscribe: (cb) => {
      if (typeof window === 'undefined') return () => {}
      const mq = window.matchMedia(query)
      mq.addEventListener('change', cb)
      return () => mq.removeEventListener('change', cb)
    },
    getSnapshot: () =>
      typeof window === 'undefined' ? false : window.matchMedia(query).matches,
    getServerSnapshot: () => false,
  }
}

function useMediaQuery(query) {
  const sub = useMemo(() => makeMediaSubscription(query), [query])
  return useSyncExternalStore(sub.subscribe, sub.getSnapshot, sub.getServerSnapshot)
}

export default function Sidebar({ mobileMode = false, onAfterSelect }) {
  const { session } = useContext(AppContext)
  const { theme, toggleTheme } = useContext(ThemeContext)
  const navigate = useNavigate()
  const location = useLocation()
  const shouldAutoRail = useMediaQuery(`(max-width: ${AUTO_RAIL_BREAKPOINT - 1}px)`)

  // User collapse preference: null = follow the responsive auto-rail
  // breakpoint, boolean = explicit user choice. Persisted under the legacy
  // "isExpanded" key (storage value 'true' = expanded, 'false' = collapsed).
  const [userCollapsedPref, setUserCollapsedPref] = useState(() => {
    if (typeof window === 'undefined') return null
    const saved = localStorage.getItem(SIDEBAR_STATE_KEY)
    if (saved === null) return null
    return saved === 'false'
  })

  const collapsed = userCollapsedPref ?? shouldAutoRail

  const handleToggle = useCallback(() => {
    setUserCollapsedPref((prev) => {
      const current = prev ?? shouldAutoRail
      const next = !current
      localStorage.setItem(SIDEBAR_STATE_KEY, String(!next))
      return next
    })
  }, [shouldAutoRail])

  const afterSelect = useCallback(() => {
    onAfterSelect?.()
  }, [onAfterSelect])

  const botMatch = useMatch('/strategy/:bid')
  const stratMatch = useMatch('/archived/strategy/:sid/*')
  const activeBid = botMatch?.params?.bid ?? null
  const activeStrId = stratMatch?.params?.sid ?? null

  const [bots, setBots] = useState([])
  const [legacyStrategies, setLegacyStrategies] = useState([])

  useEffect(() => {
    if (!session) return
    let cancelled = false

    botApi
      .list()
      .then((list) => {
        if (!cancelled) setBots(list)
      })
      .catch((err) => console.error('Error loading bots:', err))

    server
      .api('user/strategies', { includeBacktest: false })
      .then((res) => {
        if (cancelled) return
        const sorted = res ? res.sort((a, b) => (a.updated < b.updated ? 1 : -1)) : []
        setLegacyStrategies(sorted)
      })
      .catch((err) => console.error('Error loading legacy strategies:', err))

    return () => {
      cancelled = true
    }
  }, [session])

  const handleBotLifecycleEvent = useCallback((message) => {
    switch (message.event) {
      case 'bot-created':
        setBots((prev) =>
          prev.some((b) => b.bid === message.bot.bid) ? prev : [message.bot, ...prev]
        )
        break
      case 'bot-updated':
        setBots((prev) =>
          prev.map((b) => (b.bid === message.bot.bid ? message.bot : b))
        )
        break
      case 'bot-title-updated':
        setBots((prev) =>
          prev.map((b) =>
            b.bid === message.bot.bid ? { ...b, title: message.bot.title } : b
          )
        )
        break
      case 'bot-deleted':
      case 'bot-archived':
        setBots((prev) => prev.filter((b) => b.bid !== message.bot.bid))
        break
      case 'bot-restored':
        setBots((prev) =>
          prev.some((b) => b.bid === message.bot.bid) ? prev : [message.bot, ...prev]
        )
        break
      default:
        console.warn('Unknown bot lifecycle event:', message.event)
    }
  }, [])

  useSSE(
    useCallback((msg) => isBotLifecycleEvent(msg.event), []),
    handleBotLifecycleEvent
  )

  const handleBotRename = useCallback(async (id, newTitle) => {
    try {
      await botApi.update(id, newTitle)
      setBots((prev) => prev.map((b) => (b.bid === id ? { ...b, title: newTitle } : b)))
    } catch (err) {
      console.error('Error renaming bot:', err)
      alert('Failed to rename bot. Please try again.')
    }
  }, [])

  const handleBotDelete = useCallback(async (id) => {
    try {
      await botApi.delete(id)
      setBots((prev) => prev.filter((b) => b.bid !== id))
    } catch (err) {
      console.error('Error deleting bot:', err)
      alert('Failed to delete bot. Please try again.')
    }
  }, [])

  const handleLegacyDelete = useCallback(async (id) => {
    try {
      await server.api('strategy/archive', { strId: id })
      setLegacyStrategies((prev) => prev.filter((s) => s.strId !== id))
    } catch (err) {
      console.error('Error deleting legacy strategy:', err)
      alert('Failed to delete strategy. Please try again.')
    }
  }, [])

  const botStrategies = useMemo(
    () => bots.map((b) => ({ id: b.bid, name: b.title, hasRun: false })),
    [bots]
  )

  const legacyAsList = useMemo(
    () =>
      legacyStrategies.map((s) => ({ id: s.strId, name: s.title, hasRun: false })),
    [legacyStrategies]
  )

  const legacyIdSet = useMemo(
    () => new Set(legacyAsList.map((s) => s.id)),
    [legacyAsList]
  )

  const combinedStrategies = useMemo(
    () => [
      ...botStrategies,
      ...legacyAsList.map((s) => ({ ...s, dimmed: true, noRename: true })),
    ],
    [botStrategies, legacyAsList]
  )

  const isLegacy = useCallback((id) => legacyIdSet.has(id), [legacyIdSet])

  const accountMenuItems = useMemo(() => {
    const isOnPath = (prefix) =>
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)

    return [
      {
        id: 'billing',
        label: 'Billing & Usage',
        icon: Receipt,
        group: 'nav',
        active: isOnPath('/billing'),
        onSelect: () => {
          navigate('/billing')
          afterSelect()
        },
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: User,
        group: 'nav',
        active: isOnPath('/profile'),
        onSelect: () => {
          navigate('/profile')
          afterSelect()
        },
      },
      {
        id: 'contact',
        label: 'Contact us',
        icon: Mail,
        group: 'nav',
        active: isOnPath('/contact'),
        onSelect: () => {
          navigate('/contact')
          afterSelect()
        },
      },
      {
        id: 'sign-out',
        label: 'Sign Out',
        icon: LogOut,
        group: 'session',
        onSelect: () => {
          navigate('/signout')
          afterSelect()
        },
      },
    ]
  }, [location.pathname, navigate, afterSelect])

  if (!session) return null

  const brandTheme = theme === 'dark' ? 'dark' : 'light'
  const isCollapsed = mobileMode ? false : collapsed

  return (
    <SidebarShell
      collapsed={isCollapsed}
      onToggle={mobileMode ? undefined : handleToggle}
      onHomeClick={() => {
        navigate('/')
        afterSelect()
      }}
      brandLogo={<BrandLogo height={20} theme={brandTheme} />}
      brandMark={<BrandMark size={18} theme={brandTheme} />}
      strategyList={
        <StrategyList
          strategies={combinedStrategies}
          activeId={activeBid ?? activeStrId}
          onSelect={(id) => {
            navigate(isLegacy(id) ? `/archived/strategy/${id}` : `/strategy/${id}`)
            afterSelect()
          }}
          onRename={handleBotRename}
          onDelete={(id) => (isLegacy(id) ? handleLegacyDelete(id) : handleBotDelete(id))}
          header={
            <button
              type="button"
              onClick={() => {
                navigate('/strategy/new')
                afterSelect()
              }}
              className="flex items-center justify-center h-7 mx-3 mt-2 mb-1 px-3 gap-2 rounded-md leading-none bg-primary text-primary-foreground cursor-pointer transition-colors hover:bg-primary/90 motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100"
            >
              <TrendingUp
                size={13}
                strokeWidth={1.75}
                className="shrink-0 translate-y-[0.5px]"
              />
              <span className="text-label font-medium leading-none">New Strategy</span>
            </button>
          }
          emptyState={
            <div className="px-3 py-4 text-caption text-muted-foreground">
              No bots yet
            </div>
          }
        />
      }
      footer={
        <SidebarFooter
          collapsed={isCollapsed}
          creditChipSlot={
            session.subscription ? (
              <SidebarCreditChip
                planLabel={`${session.subscription.plan} plan`}
                status={session.subscription.slug === 'free' ? 'free' : 'active'}
                onClick={() => {
                  navigate('/billing')
                  afterSelect()
                }}
                onUpgradeClick={() => paywallTrigger.open()}
              />
            ) : null
          }
          themeToggleSlot={
            <ThemeToggle theme={brandTheme} onToggle={toggleTheme} />
          }
          accountSlot={
            <AccountMenu
              name={session.name ?? 'Account'}
              items={accountMenuItems}
              iconOnly={isCollapsed}
            />
          }
        />
      }
    />
  )
}
